/**
 * "Cadeia de custódia" como destino próprio da abertura (E163.F3, GUS-209 A67-10/13/14): a consulta
 * que o "Ver recibo" do Conferir abre, com o retorno ao fluxo. É a tela de custódia do HTML 6.7 — o
 * selo, o alcance, o recibo do material recebido e os originais com as cópias de trabalho — mais o
 * que o caminho curto deixou de mostrar: os recebimentos com o manifesto selado e "Como conferir por
 * fora" (E135.F2), para todos os perfis.
 *
 * A conferência de integridade e as ocorrências de custódia são da equipe (A67-09): só o perfil
 * interno as vê aqui; no banco, se aparecessem, seriam somente leitura. Nada é perdido: manifestos,
 * hashes e histórico seguem no servidor e nesta consulta.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';

import { ArvoreDaCustodia } from './ArvoreDaCustodia';
import { ConferenciaDaCustodia } from './ConferenciaDaCustodia';
import { OcorrenciasDaCustodia } from './OcorrenciasDaCustodia';
import {
  AvisoDaAbertura,
  BOTAO_DO_RODAPE,
  CartaoDaAbertura,
  Recolhivel,
  RODAPE_DA_JORNADA,
} from './PecasDaAbertura';
import { Recebimentos, Recibo } from './ReciboDaCustodia';
import { SeloDaCustodia } from './SeloDaCustodia';
import { useAberturaDoCaso } from './useAberturaDoCaso';

const t = ptBR.abertura.custodia;

export interface CadeiaDeCustodiaProps {
  client: AxiosInstance;
  caseId: string;
  /** A abertura do cliente (escritório e banco): sem a conferência e as ocorrências da equipe. */
  doCliente?: boolean;
  /** E143.F2: no banco, reconferir e resolver ficam com a equipe. */
  doBanco?: boolean;
  onVoltar: () => void;
}

function ComoComplementar(): JSX.Element {
  const [aberto, setAberto] = useState(false);
  return (
    <Recolhivel
      titulo={t.comoComplementarTitulo}
      aberto={aberto}
      onAberto={setAberto}
      testId="cadeia-como-complementar"
    >
      <p className="text-[12px] text-fg-muted">{t.comoComplementarTexto}</p>
    </Recolhivel>
  );
}

export function CadeiaDeCustodia(props: CadeiaDeCustodiaProps): JSX.Element {
  const { client, caseId } = props;
  const { dados, erro, recarregar } = useAberturaDoCaso(client, caseId, { releitura: false });
  const somenteLeitura = props.doBanco ?? false;

  let corpo: JSX.Element;
  if (dados === null) {
    corpo = erro ? (
      <p role="alert" className="text-body-sm text-danger" data-testid="custodia-erro">
        {t.erro}
      </p>
    ) : (
      <p className="text-body-sm text-fg-muted">{t.carregando}</p>
    );
  } else {
    corpo = (
      <>
        <div className="flex flex-wrap items-center justify-between gap-5">
          <h2 className="font-display text-[22px] font-semibold text-fg">{t.titulo}</h2>
          <SeloDaCustodia recibo={dados.recibo} testId="cadeia-selo" />
        </div>
        <AvisoDaAbertura
          tom="aviso"
          titulo={t.alcanceTitulo}
          texto={t.alcanceTexto}
          testId="cadeia-alcance"
        />
        <CartaoDaAbertura titulo={t.reciboTitulo} testId="cadeia-recibo">
          <Recibo recibo={dados.recibo} />
        </CartaoDaAbertura>
        <ArvoreDaCustodia recibo={dados.recibo} />
        {props.doCliente === true ? null : (
          <>
            <ConferenciaDaCustodia
              client={client}
              caseId={caseId}
              ultima={dados.recibo.ultima_conferencia}
              onConferido={recarregar}
              somenteLeitura={somenteLeitura}
            />
            <OcorrenciasDaCustodia
              client={client}
              caseId={caseId}
              ocorrencias={dados.recibo.ocorrencias ?? []}
              onResolvido={recarregar}
              somenteLeitura={somenteLeitura}
            />
          </>
        )}
        <Recebimentos client={client} caseId={caseId} recibo={dados.recibo} />
        <ComoComplementar />
      </>
    );
  }
  return (
    <div className="flex flex-col gap-5" data-testid="abertura-cadeia-de-custodia">
      {corpo}
      <div className={RODAPE_DA_JORNADA}>
        <Button
          type="button"
          variant="secondary"
          className={BOTAO_DO_RODAPE}
          data-testid="cadeia-voltar"
          onClick={props.onVoltar}
        >
          {ptBR.abertura.voltar}
        </Button>
      </div>
    </div>
  );
}
